// src/pages/admin/AdminBackups.jsx — Phase 5.4
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box, Paper, Stack, Typography, Button, Table, TableHead,
  TableRow, TableCell, TableBody, CircularProgress, Chip,
  alpha, Tooltip, Alert,
} from "@mui/material";
import BackupIcon       from "@mui/icons-material/Backup";
import DownloadIcon     from "@mui/icons-material/Download";
import RefreshIcon      from "@mui/icons-material/Refresh";
import PlayArrowIcon    from "@mui/icons-material/PlayArrow";
import CheckCircleIcon  from "@mui/icons-material/CheckCircle";
import ErrorIcon        from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import API from "../../api";
import { useToast } from "../../context/ToastContext";
import { C, gradientText } from "../../theme";

const STATUS_META = {
  idle:    { label: "Aucun backup récent", color: C.slate[500],  icon: <HourglassEmptyIcon fontSize="small" /> },
  running: { label: "Backup en cours…",     color: "#d97706",     icon: <CircularProgress size={14} /> },
  success: { label: "Dernier backup réussi", color: C.teal[600],  icon: <CheckCircleIcon fontSize="small" /> },
  failed:  { label: "Dernier backup échoué", color: "#c0392b",    icon: <ErrorIcon fontSize="small" /> },
};

function formatBytes(bytes) {
  if (!bytes) return "—";
  const units = ["o", "Ko", "Mo", "Go"];
  let i = 0, v = bytes;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return iso; }
}

export default function AdminBackups() {
  const toast = useToast();
  const [items,        setItems]        = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [triggering,   setTriggering]   = useState(false);
  const [status,       setStatus]       = useState(null);
  const [downloading,  setDownloading]  = useState(null);
  const pollRef = useRef(null);

  useEffect(() => { document.title = "Backups — Admin ExamOnline"; }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get("/admin/backups/");
      setItems(res.data.items || []);
    } catch {
      toast.error("Impossible de charger la liste des backups.");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  const loadStatus = useCallback(async () => {
    try {
      const res = await API.get("/admin/backups/status");
      setStatus(res.data);
      return res.data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => { loadList(); loadStatus(); }, [loadList, loadStatus]);

  // Poll le statut toutes les 3s tant qu'un backup est en cours
  useEffect(() => {
    if (status?.running) {
      pollRef.current = setInterval(async () => {
        const s = await loadStatus();
        if (s && !s.running) {
          clearInterval(pollRef.current);
          loadList();
          toast[s.status === "success" ? "success" : "error"](
            s.status === "success" ? "Backup terminé avec succès." : "Le backup a échoué."
          );
        }
      }, 3000);
      return () => clearInterval(pollRef.current);
    }
  }, [status?.running]); // eslint-disable-line

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      await API.post("/admin/backups/trigger");
      toast.success("Backup démarré.");
      await loadStatus();
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error("Un backup est déjà en cours.");
      } else {
        toast.error("Impossible de démarrer le backup.");
      }
    } finally {
      setTriggering(false);
    }
  };

  const handleDownload = async (filename) => {
    setDownloading(filename);
    try {
      const res = await API.get(`/admin/backups/download/${filename}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement impossible.");
    } finally {
      setDownloading(null);
    }
  };

  const meta = STATUS_META[status?.status] || STATUS_META.idle;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            <Box component="span" sx={gradientText}>Backups</Box>
          </Typography>
          <Typography color="text.secondary" sx={{ mt: .5, fontSize: "0.9rem" }}>
            Sauvegarde quotidienne automatique (3h UTC) · déclenchement manuel disponible
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => { loadList(); loadStatus(); }} disabled={loading}>
            Actualiser
          </Button>
          <Button
            variant="contained"
            startIcon={triggering || status?.running ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
            onClick={handleTrigger}
            disabled={triggering || status?.running}
          >
            {status?.running ? "Backup en cours…" : "Lancer un backup"}
          </Button>
        </Stack>
      </Stack>

      {/* Statut */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: "12px" }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ color: meta.color, display: "flex" }}>{meta.icon}</Box>
          <Typography sx={{ fontWeight: 700, color: meta.color }}>{meta.label}</Typography>
          {status?.finished_at && (
            <Typography variant="caption" color="text.secondary">
              · {formatDate(status.finished_at)}
            </Typography>
          )}
        </Stack>
        {status?.status === "failed" && status?.log_tail && (
          <Alert severity="error" sx={{ mt: 1.5, borderRadius: "8px", fontFamily: "monospace", fontSize: "0.75rem" }}>
            {status.log_tail.slice(-500)}
          </Alert>
        )}
      </Paper>

      {/* Liste des backups */}
      <Paper sx={{ borderRadius: "12px", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <BackupIcon sx={{ fontSize: 44, color: C.slate[300], mb: 1.5 }} />
            <Typography color="text.secondary" fontWeight={600}>
              Aucun backup disponible pour le moment.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: C.slate[50] }}>
                {["Fichier", "Type", "Taille", "Date", ""].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.75rem",
                    textTransform: "uppercase", letterSpacing: "0.04em", color: C.slate[600] }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(b => (
                <TableRow key={b.filename} sx={{ "&:hover": { background: C.slate[50] } }}>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                    {b.filename}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={b.type === "uploads" ? "Fichiers" : "Base de données"}
                      sx={{
                        fontWeight: 700, fontSize: "0.7rem",
                        background: alpha(b.type === "uploads" ? C.indigo[600] : C.teal[600], .1),
                        color: b.type === "uploads" ? C.indigo[600] : C.teal[600],
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.82rem", color: C.slate[600] }}>
                    {formatBytes(b.size_bytes)}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.82rem", color: C.slate[600] }}>
                    {formatDate(b.created_at)}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Télécharger">
                      <Button
                        size="small"
                        startIcon={downloading === b.filename ? <CircularProgress size={14} /> : <DownloadIcon />}
                        onClick={() => handleDownload(b.filename)}
                        disabled={downloading === b.filename}
                      >
                        Télécharger
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Alert severity="info" sx={{ mt: 2, borderRadius: "8px" }}>
        Pour restaurer un backup, contacte l'équipe technique — cette opération
        écrase la base de données actuelle et nécessite un accès serveur direct.
      </Alert>
    </Box>
  );
}